// Cost Centre — super admin view of what every AI / render / compute step costs (credits + £).
// All numbers come from the backend so the table and the worked examples share one formula set.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Calculator, Loader2, RefreshCw, RotateCcw, Save, SlidersHorizontal, Info, Bot, Hand, Building2, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";
import "./CostCentre.css";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, dp) => n.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
const gbp = (v, dp) => {
  const n = Number(v) || 0;
  if (dp !== undefined) return n < 0 ? `-£${fmt(-n, dp)}` : `£${fmt(n, dp)}`;
  if (n === 0) return "£0.00";
  if (n < 0.01) return `£${n.toFixed(4)}`;  // under a penny — never show "0.65p", it reads as 65p
  if (n < 1) return `${(n * 100).toFixed(1)}p`;
  return `£${fmt(n, 2)}`;
};
const usd = (v) => {
  const n = Number(v) || 0;
  return n === 0 ? "$0.00" : n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(n < 1 ? 3 : 2)}`;
};

const RATE_GROUPS = [
  { title: "Exchange rate", keys: [["fx_usd_to_gbp", "USD → GBP"]] },
  { title: "Creatomate plan", keys: [["creatomate_plan_name", "Plan"], ["creatomate_plan_usd", "$ per month"], ["creatomate_plan_credits", "Credits per month"], ["render_width", "Output width"], ["render_height", "Output height"], ["render_fps", "Frame rate"]] },
  { title: "OpenAI ($ per 1M tokens)", keys: [["gpt54_in", "GPT-5.4 input"], ["gpt54_out", "GPT-5.4 output"], ["gpt5x_in", "GPT-5.1/5.2 input"], ["gpt5x_out", "GPT-5.1/5.2 output"]] },
  { title: "Anthropic ($ per 1M tokens)", keys: [["claude_in", "Claude Sonnet 4.5 input"], ["claude_out", "Claude Sonnet 4.5 output"]] },
  { title: "Cloudinary / Synthesia / R2", keys: [["cloudinary_plan_name", "Cloudinary plan"], ["cloudinary_usd_per_credit", "Cloudinary $ per credit"], ["r2_usd_per_gb_month", "R2 $ per GB-month"], ["hdr_clip_seconds", "HDR clip length (s)"]] },
  { title: "Smart Select (measured)", keys: [["smart_select_in_per_clip", "Input tokens per clip"], ["smart_select_out_per_clip", "Output tokens per clip"]] },
  { title: "Other AI calls (tokens in / out)", keys: [["logo_in", "Brand analyser in"], ["logo_out", "Brand analyser out"], ["script_steps", "Script GPT-5.1 passes"], ["script_in", "Script in / pass"], ["script_out", "Script out / pass"], ["valuation_in", "Valuation in"], ["valuation_out", "Valuation out"], ["market_in", "Market update in"], ["market_out", "Market update out"]] },
  { title: "Storage per job (MB)", keys: [["storage_sneak_mb", "Sneak Peek output"], ["storage_reel_mb", "Reel output"], ["storage_sdr_proxy_mb", "SDR proxy per clip"]] },
  { title: "Payments", keys: [["stripe_pct", "Stripe % per card payment"], ["stripe_fixed_gbp", "Stripe fixed fee (£)"]] },
  { title: "People (£ per order)", keys: [["editor_pro_gbp", "Editors — per Social Pro order"], ["editor_sneak_gbp", "Editors — per Sneak Peek (Go, humans)"], ["editor_reel_gbp", "Editors — per extra reel (Go, humans)"], ["go_extra_reels_avg", "Avg extra reels per Go order"], ["visit_gbp", "Photographer visit (time in property)"]] },
  { title: "Selling prices & next tiers (£)", keys: [["price_go_gbp", "Social Go price"], ["price_pro_gbp", "Social Pro price"], ["syn_topup_gbp_per_1000", "Synthesia top-up £ per 1,000 credits"], ["creatomate_next_plan_name", "Next Creatomate plan"], ["creatomate_next_plan_usd", "Next plan $ / month"], ["creatomate_next_plan_credits", "Next plan credits / month"], ["creatomate_top_plan_name", "Top Creatomate plan"], ["creatomate_top_plan_usd", "Top plan $ / month"], ["creatomate_top_plan_credits", "Top plan credits / month"]] },
  { title: "Synthesia plans (£)", keys: [["syn_creator_yearly_gbp_month", "Creator £/month, paid yearly"], ["syn_creator_monthly_gbp", "Creator £/month, monthly"], ["syn_creator_credits_year", "Creator credits / year"], ["syn_creator_minutes_year", "Creator minutes / year"], ["syn_creator_avatars", "Creator avatar slots"], ["syn_enterprise_gbp_year", "Enterprise £ / year"], ["syn_enterprise_slots", "Enterprise avatar slots"]] },
];

const EXAMPLE_KEYS = [["ex_clips_loaded", "Clips loaded"], ["ex_clips_kept", "Clips kept"], ["ex_sneak_seconds", "Sneak Peek length (s)"], ["ex_reels", "Reels"], ["ex_reel_seconds", "Reel length (s)"]];
const AGENCY_KEYS = [["agency_orders_per_month", "Social Pro orders / month"], ["agency_go_orders_per_month", "Social Go orders / month"], ["agency_renders_per_order", "Synthesia videos / Pro order"], ["agency_video_seconds", "Avg avatar video (s)"], ["agency_post_viewing_seconds", "Post Viewing length (s)"], ["agency_post_valuation_per_month", "Post Valuation videos / month"], ["agency_post_valuation_seconds", "Post Valuation length (s)"], ["agency_avatars", "Avatars onboarded"], ["agency_onboarding_fee_gbp", "Onboarding fee per avatar (£)"], ["syn_enterprise_agencies", "Agencies on Enterprise"]];

export default function CostCentre({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRates, setShowRates] = useState(false);
  const [res, setRes] = useState("1080");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/cost-centre`);
      setData(r.data);
      setDraft(r.data.rates);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the Cost Centre");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => data && JSON.stringify(draft) !== JSON.stringify(data.rates), [draft, data]);

  const save = async (rates = draft) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/cost-centre/rates`, { rates });
      setData(r.data);
      setDraft(r.data.rates);
      toast.success("Assumptions saved — table recalculated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const r = await axios.delete(`${API}/api/admin/cost-centre/rates`);
      setData(r.data);
      setDraft(r.data.rates);
      toast.success("Back to list-price defaults");
    } catch (e) {
      toast.error("Couldn't reset");
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    const order = [];
    const by = {};
    data.lines.forEach((l) => {
      if (!by[l.group]) { by[l.group] = []; order.push(l.group); }
      by[l.group].push(l);
    });
    return order.map((g) => ({ group: g, lines: by[g] }));
  }, [data]);

  return (
    <div className="cc-page" data-testid="cost-centre-page">
      <div className="cc-inner">
        <button className="cc-back" onClick={onBack} data-testid="cost-centre-back"><ArrowLeft size={16} /> Tools</button>

        <header className="cc-hero">
          <span className="cc-eyebrow"><Calculator size={13} /> Super admin · Cost Centre</span>
          <h1 className="cc-title">What every job costs</h1>
          <p className="cc-sub">AI credits, render credits and compute, priced per action in $ and £. Every row shows its formula; the two worked examples at the bottom are built from the same rows. Change any assumption and everything recalculates.</p>
          <div className="cc-toolbar">
            <button className={`cc-chip ${showRates ? "is-on" : ""}`} onClick={() => setShowRates((v) => !v)} data-testid="cost-centre-toggle-rates">
              <SlidersHorizontal size={14} /> Assumptions & plan prices
            </button>
            <button className="cc-chip" onClick={load} disabled={loading} data-testid="cost-centre-refresh"><RefreshCw size={14} className={loading ? "cc-spin" : ""} /> Refresh</button>
            <ResToggle res={res} setRes={setRes} />
            <div className="cc-float-toggle" data-testid="cost-centre-res-float"><ResToggle res={res} setRes={setRes} suffix="float" /></div>
            {data && <span className="cc-fx" data-testid="cost-centre-fx">1 USD = £{Number(data.rates.fx_usd_to_gbp).toFixed(2)} · Creatomate {data.rates.creatomate_plan_name} (${(data.rates.creatomate_plan_usd / data.rates.creatomate_plan_credits).toFixed(4)}/credit)</span>}
          </div>
        </header>

        {showRates && data && (
          <section className="cc-rates" data-testid="cost-centre-rates">
            <div className="cc-rates-head">
              <h2>Assumptions</h2>
              <div className="cc-rates-actions">
                <button className="cc-btn ghost" onClick={reset} disabled={saving} data-testid="cost-centre-reset"><RotateCcw size={14} /> Reset to defaults</button>
                <button className="cc-btn" onClick={() => save()} disabled={!dirty || saving} data-testid="cost-centre-save">{saving ? <Loader2 size={14} className="cc-spin" /> : <Save size={14} />} Save & recalculate</button>
              </div>
            </div>
            <div className="cc-rates-grid">
              {RATE_GROUPS.map((g) => (
                <div key={g.title} className="cc-rate-group">
                  <h3>{g.title}</h3>
                  {g.keys.map(([k, label]) => (
                    <label key={k} className="cc-rate">
                      <span>{label}</span>
                      <input
                        value={draft[k] ?? ""}
                        type={typeof data.defaults[k] === "number" ? "number" : "text"}
                        step="any"
                        onChange={(e) => setDraft({ ...draft, [k]: typeof data.defaults[k] === "number" ? e.target.value : e.target.value })}
                        data-testid={`cost-centre-rate-${k}`}
                      />
                    </label>
                  ))}
                </div>
              ))}
            </div>
            {data.rates.updated_at && <p className="cc-note">Last saved {new Date(data.rates.updated_at).toLocaleString()} by {data.rates.updated_by}</p>}
          </section>
        )}

        {loading && !data && <div className="cc-loading" data-testid="cost-centre-loading"><Loader2 size={18} className="cc-spin" /> Working it out…</div>}

        {data && (
          <>
            <section className="cc-table-wrap" data-testid="cost-centre-table">
              <table className="cc-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Engine</th>
                    <th>Quantity</th>
                    <th>Credits / tokens</th>
                    <th className="num">$</th>
                    <th className="num">£</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(({ group, lines }) => (
                    <GroupRows key={group} group={group} lines={lines} res={res} />
                  ))}
                </tbody>
              </table>
            </section>

            <section className="cc-examples" data-testid="cost-centre-examples">
              <div className="cc-section-head">
                <span className="cc-eyebrow">Worked examples</span>
                <div className="cc-section-title-row">
                  <h2>One property, two ways</h2>
                  <ResToggle res={res} setRes={setRes} suffix="examples" />
                </div>
                <div className="cc-example-inputs">
                  {EXAMPLE_KEYS.map(([k, label]) => (
                    <label key={k} className="cc-ex-input">
                      <span>{label}</span>
                      <input type="number" step="any" value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`cost-centre-ex-${k}`} />
                    </label>
                  ))}
                  <label className="cc-ex-input cc-ex-check">
                    <input type="checkbox" checked={!!draft.ex_hdr} onChange={(e) => setDraft({ ...draft, ex_hdr: e.target.checked })} data-testid="cost-centre-ex-hdr" />
                    <span>Footage is HDR (iPhone)</span>
                  </label>
                  <button className="cc-btn small" onClick={() => save()} disabled={!dirty || saving} data-testid="cost-centre-ex-apply">{saving ? <Loader2 size={13} className="cc-spin" /> : <Calculator size={13} />} Recalculate</button>
                </div>
              </div>
              <div className="cc-example-grid">
                {data.examples.map((ex) => (
                  <ExampleCard key={ex.id} ex={ex} res={res} />
                ))}
              </div>
              {data.examples[1] && (
                <p className="cc-delta" data-testid="cost-centre-delta">
                  <Info size={14} /> Letting the AI choose the clips adds <strong>{gbp(data.examples[1].delta_gbp)}</strong> per property over the hand-edited route — the renders themselves cost the same either way.
                </p>
              )}
            </section>

            {data.agency && (
              <AgencySection agency={data.agency} draft={draft} setDraft={setDraft} save={save} dirty={dirty} saving={saving} />
            )}
            {data.profit && data.agency && <FirstYearSection profit={data.profit} agency={data.agency} />}
            {data.profit && <ProfitSection profit={data.profit} />}

            <section className="cc-measured" data-testid="cost-centre-measured">
              <span className="cc-eyebrow">Measured in this system</span>
              <div className="cc-measured-grid">
                {data.measured.renders.map((r) => (
                  <div key={r.kind} className="cc-measured-card">
                    <strong>{r.kind.replace("_", " ")}</strong>
                    <span>{r.count} rendered · avg {r.avg_seconds}s · avg {r.avg_clips} clips</span>
                  </div>
                ))}
                <div className="cc-measured-card"><strong>Smart Select</strong><span>{data.measured.smart_select_cached_clips} clip analyses cached (free to reuse)</span></div>
                <div className="cc-measured-card"><strong>HDR conversions</strong><span>{data.measured.sdr_proxies} clips converted & cached</span></div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

const RES_ORDER = ["1080", "720"];
const RES = { "1080": { label: "1080×1920", short: "1080p" }, "720": { label: "720×1280", short: "720p" } };

// A line/step carries its 1080p figures at the top level and 720p under `alt`; pick by the toggle.
const pickRes = (item, res) => {
  if (!item.alt) return { main: item, other: null };
  const hi = { label: RES["1080"].label, credits: item.credits, usd: item.usd, gbp: item.gbp, formula: item.formula, quantity: item.quantity };
  return res === "720" ? { main: item.alt, other: hi } : { main: hi, other: item.alt };
};

function ResToggle({ res, setRes, suffix = "" }) {
  const tid = (k) => `cost-centre-res-${k}${suffix ? `-${suffix}` : ""}`;
  return (
    <div className="cc-seg" role="tablist" aria-label="Render resolution" data-testid={`cost-centre-res-toggle${suffix ? `-${suffix}` : ""}`}>
      {RES_ORDER.map((k) => (
        <button key={k} type="button" role="tab" aria-selected={res === k} className={`cc-seg-btn ${res === k ? "is-on" : ""}`} onClick={() => setRes(k)} data-testid={tid(k)}>
          {RES[k].label}
        </button>
      ))}
    </div>
  );
}

function GroupRows({ group, lines, res }) {
  return (
    <>
      <tr className="cc-group-row"><td colSpan={6}>{group}</td></tr>
      {lines.map((l) => {
        const { main, other } = pickRes(l, res);
        return (
          <tr key={l.id} className="cc-row" data-testid={`cost-centre-row-${l.id}`}>
            <td className="cc-cell-label">
              <div className="cc-label">{l.label}{other && <span className="cc-res-tag" data-testid={`cost-centre-res-tag-${l.id}`}>{RES[res].label}</span>}</div>
              {l.notes && <div className="cc-notes">{l.notes}</div>}
              <div className="cc-formula">{main.formula || l.formula}</div>
            </td>
            <td className="cc-cell-engine">{l.engine}</td>
            <td className="cc-cell-qty">{main.quantity || l.quantity}</td>
            <td className="cc-cell-credits">
              <span key={res} className="cc-swap">{main.credits || "—"}</span>
              {other && <div className="cc-alt">{other.label} · {other.credits}</div>}
            </td>
            <td className="num cc-cell-usd">
              <span key={res} className="cc-swap">{usd(main.usd)}</span>
              {other && <div className="cc-alt">{other.label} · {usd(other.usd)}</div>}
            </td>
            <td className="num cc-cell-gbp" data-testid={`cost-centre-gbp-${l.id}`}>
              <span key={res} className="cc-swap">{gbp(main.gbp)}</span>
              {other && <div className="cc-alt cc-alt-gbp" data-testid={`cost-centre-gbp-alt-${l.id}`}>{other.label} · {gbp(other.gbp)}</div>}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ExampleCard({ ex, res }) {
  const Icon = ex.id === "auto" ? Bot : Hand;
  const hiTotal = { label: RES["1080"].label, usd: ex.total_usd, gbp: ex.total_gbp };
  const total = res === "720" && ex.alt_total ? ex.alt_total : hiTotal;
  const otherTotal = ex.alt_total ? (res === "720" ? hiTotal : ex.alt_total) : null;
  return (
    <article className={`cc-example is-${ex.id}`} data-testid={`cost-centre-example-${ex.id}`}>
      <div className="cc-example-head">
        <span className="cc-example-icon"><Icon size={18} /></span>
        <div>
          <h3>{ex.title}</h3>
          <p>{ex.subtitle}</p>
        </div>
      </div>
      <ul className="cc-steps">
        {ex.steps.map((s, i) => {
          const { main, other } = pickRes(s, res);
          return (
            <li key={i}>
              <div>
                <span className="cc-step-label">{s.label}{other && <span className="cc-res-tag">{RES[res].short}</span>}</span>
                {s.detail && <span className="cc-step-detail">{other && res === "720" ? s.alt.credits.replace("credits", "Creatomate credits") : s.detail}</span>}
              </div>
              <span className={`cc-step-cost ${main.gbp === 0 ? "is-free" : ""}`}>
                <span key={res} className="cc-swap">{main.gbp === 0 ? "free" : gbp(main.gbp)}</span>
                {other && <span className="cc-alt cc-alt-gbp">{other.label} · {gbp(other.gbp)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="cc-total">
        <span>Total per property · {total.label}</span>
        <strong key={res} className="cc-swap" data-testid={`cost-centre-total-${ex.id}`}>{gbp(total.gbp, 2)}</strong>
        <small>{usd(total.usd)}</small>
      </div>
      {otherTotal && (
        <div className="cc-total-alt" data-testid={`cost-centre-total-alt-${ex.id}`}>
          <span>Same property with {otherTotal.label} renders</span>
          <strong>{gbp(otherTotal.gbp, 2)}</strong>
          <small>{usd(otherTotal.usd)}</small>
        </div>
      )}
    </article>
  );
}

function AgencySection({ agency, draft, setDraft, save, dirty, saving }) {
  const u = agency.usage;
  const pct = Math.min(100, Math.round((u.minutes_month / Math.max(u.allowance_month, 0.01)) * 100));
  return (
    <section className="cc-agency" data-testid="cost-centre-agency">
      <div className="cc-section-head">
        <span className="cc-eyebrow"><Building2 size={13} /> Agency model</span>
        <h2>One branch on the system, per month</h2>
        <p className="cc-sub">Each agency gets its own Synthesia Creator account (5 avatars) so usage is visible per company. Synthesia is only used for <strong>NarraTour or PresenTour</strong> on a Social Pro order (1 video, 1.5 when both). <strong>Post Viewing</strong> is a Creatomate template over the property reel with the branch's generic voiceover, and <strong>Post Valuation</strong> is a one-off Creatomate brand video — neither uses Synthesia minutes. <strong>Social Go</strong> orders never touch Synthesia.</p>
        <div className="cc-example-inputs">
          {AGENCY_KEYS.map(([k, label]) => (
            <label key={k} className="cc-ex-input">
              <span>{label}</span>
              <input type="number" step="any" value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`cost-centre-agency-${k}`} />
            </label>
          ))}
          <button className="cc-btn small" onClick={() => save()} disabled={!dirty || saving} data-testid="cost-centre-agency-apply">{saving ? <Loader2 size={13} className="cc-spin" /> : <Calculator size={13} />} Recalculate</button>
        </div>
      </div>

      <div className="cc-usage" data-testid="cost-centre-agency-usage">
        <div className="cc-usage-head">
          <div>
            <strong>{u.minutes_month} min</strong> of avatar video a month
            <span className="cc-usage-sub"> · {u.orders_per_month} Pro orders × {u.renders_per_order} NarraTour/PresenTour × {u.video_seconds}s = {u.minutes_orders} min · Post Viewing, Post Valuation and {u.go_orders_per_month} Social Go orders use none</span>
          </div>
          <span className={`cc-usage-badge ${u.within_plan ? "ok" : "over"}`} data-testid="cost-centre-agency-status">
            {u.within_plan ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            {u.within_plan ? `Within plan · ${u.headroom_pct}% headroom` : `Over plan by ${Math.abs(u.headroom_minutes)} min / month`}
          </span>
        </div>
        <div className="cc-bar"><div className={`cc-bar-fill ${u.within_plan ? "" : "over"}`} style={{ width: `${pct}%` }} /></div>
        <div className="cc-usage-foot">
          <span>Allowance {u.allowance_month} min / month ({u.allowance_year} min · {u.credits_allowance_year.toLocaleString()} credits a year)</span>
          <span>≈ {u.credits_year.toLocaleString()} credits / year used</span>
          <span>Up to <strong>{u.max_orders_within_plan} Pro orders / month</strong> fit the plan at this mix</span>
          <span>{u.avatars} of {u.avatars_allowed} avatar slots used</span>
        </div>
        <div className="cc-topup" data-testid="cost-centre-agency-topup">
          <Info size={13} />
          <span>
            {u.within_plan
              ? <>Runs over? Creator sells one-off <strong>credit top-ups at £{agency.topup.gbp_per_1000.toFixed(2)} per 1,000</strong> (≈ £{agency.topup.gbp_per_minute.toFixed(2)} per avatar minute) — {agency.topup.blocks.map((b) => `${(b.credits / 1000).toFixed(0)}k £${b.gbp}`).join(" · ")}. Used after plan credits, expire at renewal — so a busy branch just buys more rather than moving plan.</>
              : <>Over by {agency.topup.over_minutes} min ≈ {agency.topup.credits_needed.toLocaleString()} credits: a one-off <strong>{(agency.topup.block / 1000).toFixed(0)}k top-up (£{agency.topup.block_gbp.toFixed(2)})</strong> covers the month (exact cost £{agency.topup.exact_gbp.toFixed(2)} at £{agency.topup.gbp_per_1000.toFixed(2)} / 1,000). Bought inside Creator, no plan change.</>}
          </span>
        </div>
      </div>

      <div className="cc-table-wrap">
        <table className="cc-table cc-plans" data-testid="cost-centre-agency-plans">
          <thead>
            <tr>
              <th>Plan</th>
              <th className="num">Synthesia £ / month</th>
              <th className="num">£ / avatar minute</th>
              <th className="num">Synthesia £ / order</th>
              <th className="num">Renders + AI £ / month</th>
              <th className="num">Total £ / month</th>
              <th className="num">Total £ / order</th>
            </tr>
          </thead>
          <tbody>
            {agency.plans.map((p, i) => (
              <tr key={p.name} className="cc-row" data-testid={`cost-centre-agency-plan-${i}`}>
                <td className="cc-cell-label"><div className="cc-label">{p.name}</div><div className="cc-notes">{p.note}</div></td>
                <td className="num cc-cell-usd">£{p.plan_gbp_month.toFixed(2)}</td>
                <td className="num cc-cell-usd">{p.synthesia_per_min_gbp ? `£${p.synthesia_per_min_gbp.toFixed(2)}` : "unlimited"}</td>
                <td className="num cc-cell-usd">{p.synthesia_per_order_gbp != null ? `£${p.synthesia_per_order_gbp.toFixed(2)}` : "—"}</td>
                <td className="num cc-cell-usd">£{p.platform_gbp_month.toFixed(2)}</td>
                <td className="num cc-cell-gbp" data-testid={`cost-centre-agency-total-${i}`}>£{p.total_gbp_month.toFixed(2)}</td>
                <td className="num cc-cell-gbp">{p.total_per_order_gbp != null ? `£${p.total_per_order_gbp.toFixed(2)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="cc-note">Renders + AI per Pro order = the "fully automatic" example ({gbp(agency.platform.base_per_order_gbp)}: Sneak Peek, reels, Smart Select, storage) + Post Viewing on Creatomate ({gbp(agency.platform.post_viewing_per_order_gbp)} for {agency.platform.post_viewing_seconds}s) = <strong>{gbp(agency.platform.pro_per_order_gbp)}</strong>; Social Go orders cost the base only; plus the monthly Post Valuation render ({gbp(agency.platform.post_valuation_month_gbp)}). Synthesia is a fixed monthly fee, so its cost per order falls as the branch does more Pro orders.</p>

      <div className="cc-ent-grid">
        <div className="cc-ent-card" data-testid="cost-centre-enterprise">
          <span className="cc-eyebrow">Enterprise alternative</span>
          <h3>£{agency.enterprise.gbp_year.toLocaleString()} / year · {agency.enterprise.slots} avatar slots · unlimited video</h3>
          <p>Cheaper than Creator paid yearly only once <strong>{agency.enterprise.breakeven_agencies_vs_yearly}</strong> agencies are on it (<strong>{agency.enterprise.breakeven_agencies_vs_monthly}</strong> vs monthly Creator). Per-agency cost by agencies onboarded:</p>
          <div className="cc-ladder">
            {agency.enterprise.ladder.map((l) => (
              <div key={l.agencies} className={`cc-ladder-step ${l.agencies === agency.enterprise.agencies ? "is-active" : ""}`}>
                <span>{l.agencies} agencies</span>
                <strong>£{l.per_agency_month.toFixed(2)}</strong>
                <small>/ agency / month</small>
              </div>
            ))}
          </div>
        </div>
        <div className="cc-ent-card" data-testid="cost-centre-onboarding">
          <span className="cc-eyebrow">Recouping the plan</span>
          <h3>Avatar onboarding fees</h3>
          {agency.onboarding.fee_gbp > 0 ? (
            <p>Charging <strong>£{agency.onboarding.fee_gbp.toFixed(0)}</strong> per avatar × {agency.onboarding.avatars} avatars = <strong>£{agency.onboarding.total_gbp.toFixed(0)}</strong> up front — that covers <strong>{agency.onboarding.months_covered_yearly}</strong> months of the yearly Creator plan or <strong>{agency.onboarding.months_covered_monthly}</strong> months of the monthly one.</p>
          ) : (
            <p>Set an onboarding fee per avatar above to see how many months of the Synthesia plan it covers. Separate Creator accounts also mean each company's usage vs orders is visible in one place.</p>
          )}
        </div>
      </div>
    </section>
  );
}


const MAX_ORDERS = 50000;
const toPos = (n) => (n <= 0 ? 0 : Math.round((Math.log(n) / Math.log(MAX_ORDERS)) * 1000));
const fromPos = (p) => (p <= 0 ? 0 : Math.min(MAX_ORDERS, Math.round(Math.exp((Math.log(MAX_ORDERS) * p) / 1000))));

function OrderSlider({ value, onChange, label, testId }) {
  return (
    <label className="cc-slider-label">
      <span className="cc-slider-head">
        <span>{label}</span>
        <input type="number" min="0" max={MAX_ORDERS} value={value} onChange={(e) => onChange(Math.max(0, Math.min(MAX_ORDERS, Number(e.target.value) || 0)))} className="cc-slider-num" data-testid={`${testId}-input`} />
      </span>
      <input type="range" min="0" max="1000" step="1" value={toPos(value)} onChange={(e) => onChange(fromPos(Number(e.target.value)))} className="cc-slider" data-testid={testId} />
      <span className="cc-slider-scale"><i>0</i><i>10</i><i>100</i><i>1k</i><i>10k</i><i>50k</i></span>
    </label>
  );
}

function ProfitSection({ profit }) {
  const [tab, setTab] = useState("go");
  const [mode, setMode] = useState("auto");          // Go: humans | auto
  const [visit, setVisit] = useState(false);         // photographer time in the property (£10)
  const [extraReels, setExtraReels] = useState(false); // Go: team also edits the 1–3 extra reels
  const [synPlan, setSynPlan] = useState("yearly");
  const [tourSec, setTourSec] = useState(null);      // avatar tour length: avg or the 1-minute max
  const [goOrders, setGoOrders] = useState(180);
  const [proOrders, setProOrders] = useState(300);
  const orders = tab === "go" ? goOrders : proOrders;
  const setOrders = tab === "go" ? setGoOrders : setProOrders;
  const p = profit[tab];
  const L = profit.labour;
  const pro = profit.pro;
  const tourLen = tourSec ?? pro.tour_seconds_avg;
  // Pro content is always edited by the editors; Go can be human-helped or fully automated
  const unit = tab === "pro" ? p.cost_auto_gbp : mode === "auto" ? p.cost_auto_gbp : p.cost_manual_gbp;
  const editingPerOrder = tab === "pro" ? L.editor_pro_gbp : mode === "auto" ? 0 : L.editor_sneak_gbp + (extraReels ? L.extra_reels_avg * L.editor_reel_gbp : 0);
  const visitPerOrder = visit ? L.visit_gbp : 0;
  const peoplePerOrder = editingPerOrder + visitPerOrder;

  // Everything below is per YEAR
  const revenue = orders * p.price_gbp;
  let renders = orders * unit, synthesia = 0, topup = 0, minutes = 0, over = 0, tourMinutes = 0;
  const allowance = pro.allowance_minutes_year;
  if (tab === "pro") {
    renders += pro.post_valuation_gbp;                                  // one Post Valuation video a year
    synthesia = synPlan === "yearly" ? pro.plan_yearly_gbp_year : pro.plan_monthly_gbp_year;
    tourMinutes = orders * pro.tours_per_order * tourLen / 60;
    minutes = tourMinutes + pro.oneoff_synthesia_minutes;               // + the two one-off voiceover exports
    over = Math.max(0, minutes - allowance);
    topup = over * pro.topup_gbp_per_minute;
  }
  const people = orders * peoplePerOrder;
  const cost = renders + synthesia + topup + people;
  const profitGbp = revenue - cost;
  const margin = revenue > 0 ? (profitGbp / revenue) * 100 : 0;
  const perOrderMargin = p.price_gbp - unit - peoplePerOrder;
  const fixed = tab === "pro" ? synthesia + pro.post_valuation_gbp : 0;
  const breakeven = tab === "pro" ? Math.ceil(fixed / Math.max(perOrderMargin, 0.01)) : 0;
  const tourCapacity = Math.floor(Math.max(0, allowance - pro.oneoff_synthesia_minutes) / (pro.tours_per_order * tourLen / 60));
  const synPerOrder = tab === "pro" && orders > 0 ? (synthesia + topup) / orders : 0;

  const c = profit.creatomate;
  const goMonth = goOrders / 12, proMonth = proOrders / 12;
  const credits = goMonth * profit.go.creatomate_credits + proMonth * profit.pro.creatomate_credits;
  const pctPlan = (credits / c.plan_credits) * 100;
  const branchesOnPlan = credits > 0 ? Math.floor(c.plan_credits / credits) : 0;
  const nextBranches = credits > 0 ? Math.floor(c.next_plan_credits / credits) : 0;
  const rendersAllMonth = goMonth * profit.go.cost_auto_gbp + proMonth * profit.pro.cost_auto_gbp;
  const topBranches = credits > 0 ? Math.floor(c.top_plan_credits / credits) : 0;
  const topRevenue = topBranches * (goMonth * profit.go.price_gbp + proMonth * profit.pro.price_gbp);
  const topPerOrder = credits > 0 ? (c.top_plan_gbp / c.top_plan_credits) * (credits / Math.max(goMonth + proMonth, 1)) : 0;
  const fmtMonth = (n) => (n >= 10 ? Math.round(n).toLocaleString() : n.toFixed(1));

  return (
    <section className="cc-profit" data-testid="cost-centre-profit">
      <div className="cc-section-head">
        <span className="cc-eyebrow"><TrendingUp size={13} /> Profit per branch · per year</span>
        <div className="cc-section-title-row">
          <h2>Orders in, profit out</h2>
          <div className="cc-seg" role="tablist">
            <button role="tab" aria-selected={tab === "go"} className={`cc-seg-btn ${tab === "go" ? "is-on" : ""}`} onClick={() => setTab("go")} data-testid="cost-centre-profit-tab-go">Social Go · £{profit.go.price_gbp}</button>
            <button role="tab" aria-selected={tab === "pro"} className={`cc-seg-btn ${tab === "pro" ? "is-on" : ""}`} onClick={() => setTab("pro")} data-testid="cost-centre-profit-tab-pro">Social Pro · £{profit.pro.price_gbp}</button>
          </div>
        </div>
        <p className="cc-sub">{tab === "pro"
          ? <>Every Social Pro order = <strong>1 avatar tour</strong> (NarraTour or PresenTour, up to 1 minute, Synthesia) + <strong>everything in Social Go</strong> (Sneak Peek + {pro.reels_avg} reels, edited automatically) + a <strong>Post Viewing</strong> render, cut by the editing team (£{L.editor_pro_gbp.toFixed(2)} an order). Once a year per branch: <strong>1 Post Valuation video</strong> and the <strong>Post Viewing voiceover</strong> — one Synthesia export each, reused on every listing. Drag the slider for the year's orders.</>
          : <>Like-for-like Sneak Peek: humans vs AI. Flip the reels chip to add the {L.extra_reels_avg.toFixed(0)} extra reels a Go order averages — the team edits them by hand, the AI cuts them from the Sneak Peek for free. Figures are for a year of orders.</>} HDR conversion is left out on purpose — capture is being switched off.</p>
      </div>

      <div className="cc-profit-card">
        <div className="cc-profit-controls">
          <OrderSlider value={orders} onChange={setOrders} label={`${orders.toLocaleString()} ${tab === "go" ? "Social Go" : "Social Pro"} orders / year · ${fmtMonth(orders / 12)} a month`} testId={`cost-centre-profit-slider-${tab}`} />
          {tab === "go" ? (
            <div className="cc-seg" role="radiogroup" aria-label="Edit mode">
              <button role="radio" aria-checked={mode === "manual"} className={`cc-seg-btn ${mode === "manual" ? "is-on" : ""}`} onClick={() => setMode("manual")} data-testid="cost-centre-profit-mode-manual"><Hand size={12} /> Humans helping</button>
              <button role="radio" aria-checked={mode === "auto"} className={`cc-seg-btn ${mode === "auto" ? "is-on" : ""}`} onClick={() => setMode("auto")} data-testid="cost-centre-profit-mode-auto"><Bot size={12} /> Fully automated</button>
            </div>
          ) : (
            <div className="cc-seg" role="radiogroup" aria-label="Who does the work">
              <button role="radio" aria-checked={!visit} className={`cc-seg-btn ${!visit ? "is-on" : ""}`} onClick={() => setVisit(false)} data-testid="cost-centre-profit-pro-editors"><Hand size={12} /> Editors £{L.editor_pro_gbp.toFixed(2)}</button>
              <button role="radio" aria-checked={visit} className={`cc-seg-btn ${visit ? "is-on" : ""}`} onClick={() => setVisit(true)} data-testid="cost-centre-profit-pro-editors-visit"><Hand size={12} /> Editors + visit £{(L.editor_pro_gbp + L.visit_gbp).toFixed(2)}</button>
            </div>
          )}
          {tab === "go" && (
            <div className="cc-toggles">
              <button type="button" className={`cc-chip ${visit ? "is-on" : ""}`} aria-pressed={visit} onClick={() => setVisit((v) => !v)} data-testid="cost-centre-profit-visit-toggle">{visit ? <CheckCircle2 size={13} /> : <Info size={13} />} Photographer visit £{L.visit_gbp.toFixed(0)}</button>
              <button type="button" className={`cc-chip ${extraReels ? "is-on" : ""}`} aria-pressed={extraReels} onClick={() => setExtraReels((v) => !v)} data-testid="cost-centre-profit-reels-toggle">{extraReels ? <CheckCircle2 size={13} /> : <Info size={13} />} + {L.extra_reels_avg.toFixed(0)} extra reels / order</button>
            </div>
          )}
          {tab === "pro" && (
            <>
              <div className="cc-seg" role="radiogroup" aria-label="Synthesia plan">
                <button role="radio" aria-checked={synPlan === "yearly"} className={`cc-seg-btn ${synPlan === "yearly" ? "is-on" : ""}`} onClick={() => setSynPlan("yearly")} data-testid="cost-centre-profit-syn-yearly">Creator yearly £{pro.plan_yearly_gbp_year.toFixed(0)}/yr</button>
                <button role="radio" aria-checked={synPlan === "monthly"} className={`cc-seg-btn ${synPlan === "monthly" ? "is-on" : ""}`} onClick={() => setSynPlan("monthly")} data-testid="cost-centre-profit-syn-monthly">Monthly £{pro.plan_monthly_gbp}/mo</button>
              </div>
              <div className="cc-seg" role="radiogroup" aria-label="Avatar tour length">
                <button role="radio" aria-checked={tourLen === pro.tour_seconds_avg} className={`cc-seg-btn ${tourLen === pro.tour_seconds_avg ? "is-on" : ""}`} onClick={() => setTourSec(pro.tour_seconds_avg)} data-testid="cost-centre-profit-tour-avg">Tour {pro.tour_seconds_avg}s avg</button>
                <button role="radio" aria-checked={tourLen === pro.tour_seconds_max} className={`cc-seg-btn ${tourLen === pro.tour_seconds_max ? "is-on" : ""}`} onClick={() => setTourSec(pro.tour_seconds_max)} data-testid="cost-centre-profit-tour-max">Tour {pro.tour_seconds_max}s max</button>
              </div>
            </>
          )}
        </div>

        <div className="cc-kpis">
          <div className="cc-kpi"><span>Revenue / yr</span><strong data-testid="cost-centre-profit-revenue">{gbp(revenue, 2)}</strong><small>{orders.toLocaleString()} × £{p.price_gbp}</small></div>
          <div className="cc-kpi"><span>Renders + AI / yr</span><strong>{gbp(renders, 2)}</strong><small>{gbp(unit)} per order{tab === "pro" ? ` (Sneak Peek + ${pro.reels_avg} reels ${gbp(pro.sneak_reels_gbp)} · Post Viewing ${gbp(pro.post_viewing_gbp)}) + ${gbp(pro.post_valuation_gbp)} Post Valuation once` : ""}</small></div>
          <div className={`cc-kpi ${peoplePerOrder > 0 ? "" : "is-muted"}`}><span>People / yr</span><strong data-testid="cost-centre-profit-people">{gbp(people, 2)}</strong><small>{peoplePerOrder > 0
            ? `${gbp(peoplePerOrder, 2)} per order · ${[editingPerOrder > 0 ? (tab === "pro" ? "editors" : extraReels ? `Sneak Peek + ${L.extra_reels_avg.toFixed(0)} reels edited` : "Sneak Peek edited") : null, visit ? "visit" : null].filter(Boolean).join(" + ")}`
            : extraReels ? `AI renders the ${L.extra_reels_avg.toFixed(0)} extra reels from the Sneak Peek cut — no editor time` : "fully automated — no editor time"}</small></div>
          {tab === "pro" && <div className="cc-kpi"><span>Synthesia / yr</span><strong data-testid="cost-centre-profit-synthesia">{gbp(synthesia + topup, 2)}</strong><small>{synPlan === "yearly" ? "Creator yearly" : "Creator monthly ×12"}{over > 0 ? ` + ${gbp(topup, 2)} top-up for ${over.toFixed(0)} min over` : " · within the 360 min"} · {gbp(synPerOrder, 2)} an order</small></div>}
          <div className={`cc-kpi is-profit ${profitGbp < 0 ? "is-loss" : ""}`}><span>Profit / yr</span><strong data-testid="cost-centre-profit-value">{gbp(profitGbp, 2)}</strong><small>{margin.toFixed(1)}% margin{tab === "pro" ? ` · breaks even at ${breakeven} order${breakeven === 1 ? "" : "s"} a year` : ""}</small></div>
        </div>

        {tab === "pro" && (
          <div className="cc-profit-track" data-testid="cost-centre-profit-synthesia-track">
            <div className="cc-track-head">
              <strong>Synthesia minutes · {minutes.toFixed(0)} of {allowance.toFixed(0)} a year</strong>
              <span>{over > 0 ? `${over.toFixed(0)} min over — ${tourCapacity.toLocaleString()} tours fit in the plan, the rest are topped up` : `${(allowance - minutes).toFixed(0)} min spare — room for ${(tourCapacity - orders).toLocaleString()} more tours before any top-up`}</span>
            </div>
            <div className="cc-bar"><div className={`cc-bar-fill ${over > 0 ? "over" : ""}`} style={{ width: `${Math.min(100, (minutes / allowance) * 100)}%` }} /></div>
            <span className="cc-note">{orders.toLocaleString()} tours × {tourLen}s = {tourMinutes.toFixed(1)} min, plus {pro.oneoff_synthesia_minutes.toFixed(1)} min for the Post Viewing voiceover and Post Valuation exports. Creator gives {allowance.toFixed(0)} min a year, so a branch can do <strong>{tourCapacity.toLocaleString()} Pro orders a year</strong> ({fmtMonth(tourCapacity / 12)} a month) at {tourLen}s before top-ups. Each order past break-even adds {gbp(perOrderMargin)} to profit (£{p.price_gbp} − {gbp(unit)} renders − {gbp(peoplePerOrder, 2)} people); once the minutes run out it's {gbp(perOrderMargin - pro.tours_per_order * tourLen / 60 * pro.topup_gbp_per_minute)} an order with top-ups at £{pro.topup_gbp_per_1000} / 1,000 credits.</span>
          </div>
        )}
      </div>

      <div className="cc-creatomate-note" data-testid="cost-centre-creatomate-threshold">
        <span className="cc-eyebrow">Creatomate thresholds</span>
        <p>
          Creatomate is one NicheCom-wide plan, not per branch. This branch at <strong>{fmtMonth(goMonth)} Go + {fmtMonth(proMonth)} Pro</strong> a month ({goOrders.toLocaleString()} + {proOrders.toLocaleString()} a year) burns <strong>{Math.round(credits).toLocaleString()} credits</strong> a month — {pctPlan.toFixed(1)}% of the {c.plan_name} plan ({c.plan_credits.toLocaleString()} credits, £{c.plan_gbp}/month). {branchesOnPlan >= 1
            ? <>That plan carries <strong>{branchesOnPlan.toLocaleString()} branches</strong> at this volume — or {c.go_orders_capacity.toLocaleString()} Go / {c.pro_orders_capacity.toLocaleString()} Pro orders a month in total. Past that, <strong>{c.next_plan_name}</strong> is £{c.next_plan_gbp}/month for {c.next_plan_credits.toLocaleString()} credits (≈ {nextBranches.toLocaleString()} branches), i.e. +£{(c.next_plan_gbp - c.plan_gbp).toFixed(0)}/month against ≈ {gbp(rendersAllMonth * branchesOnPlan, 0)} of render cost already priced into {branchesOnPlan} branches' orders — the upgrade is noise at that volume.</>
            : <>That's more than the whole plan for this one branch — it would take <strong>{Math.ceil(credits / c.plan_credits).toLocaleString()} Growth plans</strong>, or {nextBranches >= 1 ? <><strong>{c.next_plan_name}</strong> (£{c.next_plan_gbp}/month, {c.next_plan_credits.toLocaleString()} credits) which carries {nextBranches.toLocaleString()} branch{nextBranches === 1 ? "" : "es"} like it</> : <>{Math.ceil(credits / c.next_plan_credits).toLocaleString()}× {c.next_plan_name}</>}. Render cost stays priced into every order at {gbp(profit.go.cost_auto_gbp)}–{gbp(profit.pro.cost_auto_gbp)} each, so the plan tier is a cash-flow question, not a margin one.</>}
        </p>
        <p className="cc-top-plan" data-testid="cost-centre-creatomate-top-plan">
          Top of the ladder is <strong>{c.top_plan_name}</strong>: ${c.top_plan_usd.toLocaleString()} (£{c.top_plan_gbp.toLocaleString()}) a month for {c.top_plan_credits.toLocaleString()} credits — {topBranches >= 1
            ? <>enough for <strong>{topBranches.toLocaleString()} branches</strong> at {fmtMonth(goMonth)} Go + {fmtMonth(proMonth)} Pro a month each, roughly £{Math.round(topRevenue).toLocaleString()} of monthly order revenue against a {gbp(c.top_plan_gbp, 0)} render bill ({topPerOrder < 1 ? `${(topPerOrder * 100).toFixed(1)}p` : gbp(topPerOrder, 2)} a video set). Beyond that Creatomate quotes custom.</>
            : <>and even that covers only <strong>{((c.top_plan_credits / credits) * 100).toFixed(0)}%</strong> of this one branch's {Math.round(credits).toLocaleString()} credits — {fmtMonth(goMonth + proMonth)} orders a month from one branch is custom-pricing territory (≈ {Math.ceil(credits / c.top_plan_credits).toLocaleString()} Beyond plans), and about £{Math.round(goMonth * profit.go.price_gbp + proMonth * profit.pro.price_gbp).toLocaleString()} of monthly revenue.</>}
        </p>
      </div>
    </section>
  );
}

function FirstYearSection({ profit, agency }) {
  const [basis, setBasis] = useState("month");           // orders entered per month or for the whole year
  const [orders, setOrders] = useState(10);
  const [fullCreatomate, setFullCreatomate] = useState(false);
  const [fee, setFee] = useState(agency.onboarding.fee_gbp);
  const [avatars, setAvatars] = useState(agency.onboarding.avatars);
  useEffect(() => { setFee(agency.onboarding.fee_gbp); setAvatars(agency.onboarding.avatars); }, [agency.onboarding.fee_gbp, agency.onboarding.avatars]);
  const p = profit.pro, c = profit.creatomate;
  const price = p.price_gbp;
  const maxAvatars = agency.usage.avatars_allowed || 5;
  const renderPerOrder = p.creatomate_credits * c.gbp_per_credit;
  const aiPerOrder = Math.max(0, p.cost_auto_gbp - renderPerOrder);

  const ordersMonth = basis === "month" ? orders : orders / 12;
  const ordersYear = basis === "month" ? orders * 12 : orders;
  const minutes = ordersMonth * p.synthesia_minutes_per_order;
  const topupMonth = Math.max(0, minutes - p.allowance_minutes_month) * p.topup_gbp_per_minute;
  const onboarding = fee * avatars;                       // charged once, up front

  const revenue = ordersYear * price + onboarding;
  const synthesia = p.plan_yearly_gbp_month * 12 + topupMonth * 12;
  const creatomate = fullCreatomate ? c.plan_gbp * 12 : ordersYear * renderPerOrder;
  const ai = ordersYear * aiPerOrder + p.post_valuation_month_gbp * 12;
  const cost = synthesia + creatomate + ai;
  const profitYear = revenue - cost;
  const margin = revenue > 0 ? (profitYear / revenue) * 100 : 0;

  const fixed = p.plan_yearly_gbp_month * 12 + (fullCreatomate ? c.plan_gbp * 12 : 0) + p.post_valuation_month_gbp * 12;
  const fixedNet = Math.max(0, fixed - onboarding);       // onboarding fees pay the seat down on day one
  const perOrderMargin = price - aiPerOrder - (fullCreatomate ? 0 : renderPerOrder);
  const sweetSpot = perOrderMargin > 0 ? Math.ceil(fixedNet / perOrderMargin) : Infinity;
  const breakevenMonth = sweetSpot === 0 ? 1 : ordersMonth > 0 && sweetSpot !== Infinity ? Math.ceil(sweetSpot / ordersMonth) : null;

  const months = Array.from({ length: 12 }, (_, i) => ordersMonth * (i + 1) * perOrderMargin - topupMonth * (i + 1) - fixed + onboarding);
  const maxAbs = Math.max(...months.map(Math.abs), 1);
  const ordersLabel = basis === "month" ? `${orders} Social Pro orders / month` : `${orders} Social Pro orders in the year (≈ ${(orders / 12).toFixed(1)} a month)`;

  return (
    <section className="cc-first-year" data-testid="cost-centre-first-year">
      <div className="cc-section-head">
        <span className="cc-eyebrow"><Building2 size={13} /> Worked example · one branch's first year on Social Pro</span>
        <div className="cc-section-title-row">
          <h2>Paying off the Synthesia year</h2>
          <button type="button" className={`cc-chip ${fullCreatomate ? "is-on" : ""}`} onClick={() => setFullCreatomate((v) => !v)} aria-pressed={fullCreatomate} data-testid="cost-centre-first-year-creatomate-toggle">
            {fullCreatomate ? <CheckCircle2 size={13} /> : <Info size={13} />} {fullCreatomate ? `Carrying the whole ${c.plan_name} plan (£${(c.plan_gbp * 12).toFixed(0)}/yr)` : `Creatomate at fair share (${(renderPerOrder * 100).toFixed(1)}p an order)`}
          </button>
        </div>
        <p className="cc-sub">Branch signs up to Social Pro, we buy their Synthesia Creator seat at the yearly rate (£{(p.plan_yearly_gbp_month * 12).toFixed(0)} up front, £{p.plan_yearly_gbp_month}/month), charge <strong>{avatars}</strong> avatar onboarding{avatars === 1 ? "" : "s"} at <strong>£{fee}</strong>, and they order <strong>{basis === "month" ? `${orders} a month` : `${orders} over the year`}</strong>. Onboarding fees are pre-filled from the section above. Creatomate is shown as this branch's share of our plan by default — flip the chip to load the entire {c.plan_name} bill onto them as a worst case.</p>
      </div>
      <div className="cc-profit-card">
        <div className="cc-profit-controls">
          <label className="cc-slider-label">
            <span className="cc-slider-head">
              <span>{ordersLabel}</span>
              <span className="cc-inline-controls">
                <span className="cc-seg" role="radiogroup" aria-label="Order basis">
                  <button type="button" role="radio" aria-checked={basis === "month"} className={`cc-seg-btn ${basis === "month" ? "is-on" : ""}`} onClick={() => { if (basis !== "month") { setBasis("month"); setOrders(Math.max(0, Math.round(orders / 12))); } }} data-testid="cost-centre-first-year-basis-month">Per month</button>
                  <button type="button" role="radio" aria-checked={basis === "year"} className={`cc-seg-btn ${basis === "year" ? "is-on" : ""}`} onClick={() => { if (basis !== "year") { setBasis("year"); setOrders(orders * 12); } }} data-testid="cost-centre-first-year-basis-year">Whole year</button>
                </span>
                <input type="number" min="0" max={basis === "month" ? 500 : 6000} value={orders} onChange={(e) => setOrders(Math.max(0, Math.min(basis === "month" ? 500 : 6000, Number(e.target.value) || 0)))} className="cc-slider-num" data-testid="cost-centre-first-year-input" />
              </span>
            </span>
            <input type="range" min="0" max={basis === "month" ? 100 : 1200} step="1" value={Math.min(orders, basis === "month" ? 100 : 1200)} onChange={(e) => setOrders(Number(e.target.value))} className="cc-slider" data-testid="cost-centre-first-year-slider" />
          </label>
          <div className="cc-onboard" data-testid="cost-centre-first-year-onboarding">
            <label><span>Onboarding fee / avatar (£)</span><input type="number" min="0" step="5" value={fee} onChange={(e) => setFee(Math.max(0, Number(e.target.value) || 0))} className="cc-slider-num" data-testid="cost-centre-first-year-fee" /></label>
            <label><span>Avatars (max {maxAvatars})</span><input type="number" min="0" max={maxAvatars} value={avatars} onChange={(e) => setAvatars(Math.max(0, Math.min(maxAvatars, Number(e.target.value) || 0)))} className="cc-slider-num" data-testid="cost-centre-first-year-avatars" /></label>
            <span className="cc-onboard-total">= <strong>{gbp(onboarding, 2)}</strong> up front</span>
          </div>
          <div className="cc-sweet" data-testid="cost-centre-first-year-sweet-spot">
            <span>Sweet spot</span>
            <strong>{sweetSpot === Infinity ? "—" : `${sweetSpot} orders`}</strong>
            <small>{sweetSpot === Infinity ? "price doesn't cover the per-order cost" : sweetSpot === 0 ? "onboarding fees alone cover the year's fixed bills" : `pays the year's fixed bills after onboarding · ${breakevenMonth ? (breakevenMonth <= 12 ? `month ${breakevenMonth} at ${ordersMonth.toFixed(ordersMonth % 1 ? 1 : 0)} a month` : `not within the year at ${ordersMonth.toFixed(1)} a month`) : "set some orders"}`}</small>
          </div>
        </div>
        <div className="cc-kpis">
          <div className="cc-kpi"><span>Revenue / yr</span><strong data-testid="cost-centre-first-year-revenue">{gbp(revenue, 2)}</strong><small>{ordersYear.toLocaleString()} orders × £{price}{onboarding > 0 ? ` + ${gbp(onboarding, 0)} onboarding` : ""}</small></div>
          {onboarding > 0 && <div className="cc-kpi"><span>Onboarding</span><strong data-testid="cost-centre-first-year-onboarding-total">{gbp(onboarding, 2)}</strong><small>{avatars} × £{fee} · paid in month 1</small></div>}
          <div className="cc-kpi"><span>Synthesia / yr</span><strong>{gbp(synthesia, 2)}</strong><small>Creator yearly £{(p.plan_yearly_gbp_month * 12).toFixed(0)}{topupMonth > 0 ? ` + ${gbp(topupMonth * 12, 2)} top-ups` : " · within allowance"}</small></div>
          <div className="cc-kpi"><span>Creatomate / yr</span><strong>{gbp(creatomate, 2)}</strong><small>{fullCreatomate ? `${c.plan_name} plan, all of it` : `${(ordersYear * p.creatomate_credits).toLocaleString()} credits at fair share`}</small></div>
          <div className="cc-kpi"><span>AI & storage / yr</span><strong>{gbp(ai, 2)}</strong><small>Smart Select, R2, Post Valuation render</small></div>
          <div className={`cc-kpi is-profit ${profitYear < 0 ? "is-loss" : ""}`}><span>Profit / yr</span><strong data-testid="cost-centre-first-year-profit">{gbp(profitYear, 2)}</strong><small>{margin.toFixed(1)}% margin</small></div>
        </div>
        <div className="cc-months" data-testid="cost-centre-first-year-months">
          {months.map((v, i) => (
            <div key={i} className={`cc-month ${v < 0 ? "is-neg" : ""}`} title={`Month ${i + 1}: ${gbp(v, 2)} cumulative`}>
              <div className="cc-month-bar"><i style={{ height: `${Math.max(4, (Math.abs(v) / maxAbs) * 100)}%` }} /></div>
              <span>M{i + 1}</span>
            </div>
          ))}
          <p className="cc-note">Cumulative profit by month — Synthesia year paid on day one, onboarding fees received in month 1. Bars go lime the month the branch has covered its fixed bills.</p>
        </div>
      </div>
    </section>
  );
}
